import { Auth0Client } from '@auth0/auth0-spa-js';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { suspend } from 'suspend-react';

////////////////////////////////////////////////////////////////////////////////
// AuthContext
import { jsx as _jsx } from "react/jsx-runtime";
const AuthContext = /*#__PURE__*/createContext(null);
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth() may be used only in the context of a <AuthProvider> component.');
  }
  return context;
}

////////////////////////////////////////////////////////////////////////////////
// AuthClient

class AuthClient {
  /**
   * @internal
   */

  constructor({
    clientId,
    redirectUri = window.location.origin,
    cacheLocation = 'memory',
    audience,
    scope = 'openid profile email',
    domain
  }) {
    this.auth0 = void 0;
    this.auth0 = new Auth0Client({
      clientId,
      domain,
      cacheLocation,
      authorizationParams: {
        scope,
        audience,
        redirect_uri: redirectUri
      }
    });
  }
  async getToken(options) {
    return this.auth0.getTokenSilently({
      cacheMode: options !== null && options !== void 0 && options.ignoreCache ? 'off' : 'on'
    });
  }
  async getUser() {
    const user = await this.auth0.getUser();
    if (!user) {
      throw new Error('user is not defined');
    }
    return {
      userId: String(user['https://acast.com/userId']),
      auth0Id: String(user.sub),
      email: String(user.email),
      avatar: user.picture || null,
      isImpersonating: !!user['https://acast.com/impersonatedBy']
    };
  }
  async logout() {
    await this.auth0.logout({
      logoutParams: {
        returnTo: 'https://acast.com'
      }
    });
  }
}

////////////////////////////////////////////////////////////////////////////////
// AuthProvider

function AuthProvider({
  children,
  client
}) {
  const authState = suspend(() => readAuthState(client), [cacheKey]);
  const logout = useCallback(() => {
    client.logout();
  }, [client]);
  const getToken = useCallback(async opts => {
    try {
      const token = await client.getToken(opts);
      return token;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error) {
      if (RECOVERABLE_ERRORS.includes(error.error)) {
        await client.auth0.loginWithRedirect({
          appState: {
            redirectUrl: window.location.pathname
          }
        });
        // Keep promise in pending state until user is redirected.
        return new Promise(() => undefined);
      }
      throw error;
    }
  }, [client]);
  const context = useMemo(() => Object.assign({}, authState, {
    logout,
    getToken
  }), [authState, logout, getToken]);
  return /*#__PURE__*/_jsx(AuthContext.Provider, {
    value: context,
    children: children
  });
}
async function readAuthState(client) {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('code') && searchParams.has('state')) {
      const _await$client$auth0$h = await client.auth0.handleRedirectCallback(),
        appState = _await$client$auth0$h.appState;
      window.history.replaceState({}, document.title, (appState === null || appState === void 0 ? void 0 : appState.redirectUrl) || window.location.pathname);

      // We need to immediately after login do a silent authentication to
      // get a stable session ID. We need a stable ID for impersonation to work.
      // Temporarily disable cache to try fix the issue bug/sc-79095/dropped-connection-after-setting-password
      await client.getToken({
        ignoreCache: false
      });
    } else {
      await client.getToken();
    }
    const user = await client.getUser();
    return user;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error) {
    if (
    // Redirect to login if unable to verify code and state.
    error.message === 'Invalid state' ||
    // Redirect to login if error is recoverable.
    RECOVERABLE_ERRORS.includes(error.error)) {
      await client.auth0.loginWithRedirect({
        appState: {
          redirectUrl: window.location.pathname
        }
      });
      // Keep suspending until user is redirected.
      return new Promise(() => undefined);
    }
    throw error;
  }
}
const RECOVERABLE_ERRORS = ['login_required', 'consent_required', 'interaction_required', 'account_selection_required', 'access_denied'];
const cacheKey = Symbol();

////////////////////////////////////////////////////////////////////////////////
// Exports

export { useAuth, AuthProvider, AuthContext, AuthClient };